import React, { createContext, useContext } from "react";
import { API } from "taskpane/constants/api";
import { useQueryConfig } from "taskpane/hooks/queries";
import { IQueryConfigResponse } from "taskpane/hooks/queries/useQueryConfig";
import { IQueryResponse } from "taskpane/models/interfaces";
import { OnReady } from "taskpane/models/types";
import { getErrorNotFoundConfigProperty } from "taskpane/resources";

interface IApiProvider {
  children: React.ReactNode;
}

export interface IApiParams {
  apiUrl: string;
  apiVersion: number;
  errorMessage: string;
}

export interface IApiContext extends IApiParams {
  fetchConfig: (domain: string, callback: OnReady<IApiParams>) => void;
}

export const ApiContext = createContext<IApiContext>({
  apiUrl: "",
  apiVersion: 0,
  errorMessage: "",
  fetchConfig: (_domain, _callback) => {},
});

export const useApiContext = () => useContext(ApiContext);

const ApiProvider: React.FC<IApiProvider> = ({ children }: IApiProvider) => {
  const [apiUrl, setApiUrl] = React.useState<string>("");
  const [apiVersion, setApiVersion] = React.useState<number>(null);
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const fetchConfig = (domain: string, callback: OnReady<IApiParams>): void => {
    useQueryConfig(domain, ({ response, errorMessage }: IQueryResponse<IQueryConfigResponse>) => {
      const apiParams: IApiParams = {
        apiUrl: "",
        apiVersion: 0,
        errorMessage: "",
      };

      if (errorMessage) {
        setErrorMessage(errorMessage);

        return callback({
          ...apiParams,
          errorMessage,
        });
      }

      const mainApi: string = response["api-urls"]?.["main-api"];

      if (!mainApi) {
        const error: string = getErrorNotFoundConfigProperty("main-api");

        setErrorMessage(error);

        return callback({
          ...apiParams,
          errorMessage: error,
        });
      }

      const apiVersion: number = response["api-version"] || API.defaultApiVersion;
      const apiUrl: string = `${mainApi}/api/v${apiVersion}`;

      setApiVersion(apiVersion);
      setApiUrl(apiUrl);

      callback({
        ...apiParams,
        apiUrl,
        apiVersion,
      });
    });
  };

  return (
    <ApiContext.Provider value={{ apiUrl, apiVersion, errorMessage, fetchConfig }}>{children}</ApiContext.Provider>
  );
};

export default ApiProvider;
